import React, { useState, useEffect, useRef, useCallback } from "react";
import { Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { BASE_URL } from "../api/url";
import { getSalesClientDetailsList } from "../../services/sales";
import PageHeader from "../Ui_Components/PageHeader";
import Pagination from "../Ui_Components/Pagination";
import CustomDropdown, { DropdownOption } from "../Ui_Components/CustomDropdown";
import useCustomScrollbar from "../styles/useCustomScrollbar";

// ── Types ─────────────────────────────────────────────────────────────────────

type AssignmentFilter = "assigned" | "unassigned" | "all";

interface PrimaryNutritionist {
  nutrist_id: number;
  role: string;
  full_name: string;
}

interface LatestSubscription {
  plan_name: string;
  status: string;
  start_date: string;
  end_date: string;
  duration_days: number;
}

interface SalesClientItem {
  user_id: number;
  full_name: string;
  profile: string | null;
  goal: string | null;
  lifecycle: "client" | "churned";
  client_since: string;
  is_assigned: boolean;
  primary_nutritionist: PrimaryNutritionist | null;
  latest_subscription: LatestSubscription | null;
}

interface SalesClientListResponse {
  total_count: number;
  page: number;
  page_size: number;
  total_pages: number;
  results: SalesClientItem[];
}

// ── Global page cache ─────────────────────────────────────────────────────────
const salesClientPageCache = new Map<number, SalesClientItem[]>();
let cachedSalesClientTotalPages = 1;
let cachedSalesClientTotalCount = 0;
let cachedSalesClientCacheKey   = "";

// ── Filter options ────────────────────────────────────────────────────────────
const ASSIGNMENT_OPTIONS: DropdownOption[] = [
  { value: "all",        label: "All"        },
  { value: "assigned",   label: "Assigned"   },
  { value: "unassigned", label: "Unassigned" },
];

// ── Helpers ───────────────────────────────────────────────────────────────────
const trimPlanName = (name: string): string =>
  name.replace(/\s*\([^)]*\)/g, "").trim();

const formatDate = (iso: string): string => {
  const d     = new Date(iso);
  const day   = d.getDate();
  const month = d.toLocaleString("en-US", { month: "short" });
  const year  = d.getFullYear();
  return `${day} ${month}, ${year}`;
};

const formatDuration = (days: number | undefined | null): string => {
  if (!days) return "-";
  if (days % 30 === 0) {
    const m = days / 30;
    return `${m} ${m === 1 ? "month" : "months"}`;
  }
  if (days % 7 === 0) {
    const w = days / 7;
    return `${w} ${w === 1 ? "week" : "weeks"}`;
  }
  return `${days} days`;
};

const makeCacheKey = (assignment: AssignmentFilter, search: string): string =>
  `${assignment}::${search.trim().toLowerCase()}`;

// ── Component ─────────────────────────────────────────────────────────────────
const SalesClientPage: React.FC = () => {
  useCustomScrollbar();
  const navigate = useNavigate();

  const [clients, setClients]               = useState<SalesClientItem[]>([]);
  const [loading, setLoading]               = useState(true);
  const [currentPage, setCurrentPage]       = useState(1);
  const [totalPages, setTotalPages]         = useState(1);
  const [totalCount, setTotalCount]         = useState(0);
  const [searchQuery, setSearchQuery]       = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [assignment, setAssignment]         = useState<AssignmentFilter>("all");

  const prefetchingPages = useRef(new Set<number>());
  const debounceTimer    = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Debounced search input ───────────────────────────────────────────────────
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearchQuery(val);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setDebouncedSearch(val);
      setCurrentPage(1);
    }, 400);
  };

  // ── Fetch ───────────────────────────────────────────────────────────────────
  const fetchClients = useCallback(async (
    page: number,
    assignmentFilter: AssignmentFilter,
    search: string,
    isPrefetch = false
  ): Promise<void> => {
    const cacheKey = makeCacheKey(assignmentFilter, search);

    // Bust cache when filter or search changes
    if (cachedSalesClientCacheKey !== cacheKey) {
      salesClientPageCache.clear();
      cachedSalesClientTotalPages = 1;
      cachedSalesClientTotalCount = 0;
      cachedSalesClientCacheKey   = cacheKey;
    }

    if (salesClientPageCache.has(page)) {
      if (!isPrefetch) {
        setClients(salesClientPageCache.get(page)!);
        setTotalPages(cachedSalesClientTotalPages);
        setTotalCount(cachedSalesClientTotalCount);
        setLoading(false);
      }
      return;
    }

    if (isPrefetch && prefetchingPages.current.has(page)) return;

    try {
      if (isPrefetch) {
        prefetchingPages.current.add(page);
      } else {
        setLoading(true);
      }

      const data: SalesClientListResponse = await getSalesClientDetailsList(
        "all",
        assignmentFilter,
        page,
        10,
        search   // ← only search_user is sent; empty string = param omitted
      );

      salesClientPageCache.set(page, data.results);
      cachedSalesClientTotalPages = data.total_pages;
      cachedSalesClientTotalCount = data.total_count;

      if (!isPrefetch) {
        setClients(data.results);
        setTotalPages(data.total_pages);
        setTotalCount(data.total_count);
        setLoading(false);
      }

      if (!isPrefetch && page < data.total_pages) {
        setTimeout(
          () => fetchClients(page + 1, assignmentFilter, search, true),
          100
        );
      }
    } catch (err) {
      console.error(`Error fetching sales clients page ${page}:`, err);
      if (!isPrefetch) setLoading(false);
    } finally {
      if (isPrefetch) prefetchingPages.current.delete(page);
    }
  }, []);

  // Trigger fetch on page / filter / search change
  useEffect(() => {
    fetchClients(currentPage, assignment, debouncedSearch);
  }, [currentPage, assignment, debouncedSearch, fetchClients]);

  // ── Assignment filter handler ────────────────────────────────────────────────
  const handleAssignmentChange = (val: string) => {
    salesClientPageCache.clear();
    cachedSalesClientTotalPages = 1;
    cachedSalesClientTotalCount = 0;
    cachedSalesClientCacheKey   = "";
    setSearchQuery("");
    setDebouncedSearch("");
    setCurrentPage(1);
    setAssignment(val as AssignmentFilter);
  };

  const isSearchActive = debouncedSearch.trim().length > 0;
  const displayCount   = cachedSalesClientTotalCount || totalCount;

  // ── Navigation ───────────────────────────────────────────────────────────────
  const handleClientClick = (userId: number) => {
    navigate(`/sales-clients/${userId}`);
  };

  // ── Avatar ────────────────────────────────────────────────────────────────────
  const getUserAvatar = (client: SalesClientItem) => {
    if (client.profile) {
      return (
        <img
          src={`${BASE_URL}${client.profile}`}
          alt={client.full_name}
          className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover flex-shrink-0"
        />
      );
    }
    const initials = client.full_name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
    return (
      <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-purple-400 to-purple-500 flex items-center justify-center text-white text-sm sm:text-base font-semibold flex-shrink-0">
        {initials}
      </div>
    );
  };

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="h-screen flex flex-col bg-[#F7F7FA]">
      <PageHeader title="Clients" />

      <div className="px-4 sm:px-6 lg:px-8 pb-4 flex-1 flex flex-col overflow-hidden">
        <div className="bg-white rounded-[6px] shadow-sm flex-1 flex flex-col border-[0.5px] border-[#F3F4F6] overflow-hidden">

          {/* Count + filter + search */}
          <div className="p-4 sm:px-6 sm:py-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 flex-shrink-0">
            <h3 className="text-lg sm:text-xl font-semibold text-gray-900">
              Total clients ({displayCount})
            </h3>

            <div className="flex items-center gap-3 w-full sm:w-auto">
              <CustomDropdown
                options={ASSIGNMENT_OPTIONS}
                value={assignment}
                onChange={handleAssignmentChange}
                placeholder="Filter"
                className="w-[140px] flex-shrink-0"
              />

              <div className="relative flex-1 sm:flex-none">
                <input
                  type="text"
                  placeholder="Search by client name"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  className="w-full sm:w-auto pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-teal-400 text-sm"
                />
                <Search className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="flex-1 overflow-hidden flex flex-col">
            {loading ? (
              <div className="flex items-center justify-center flex-1">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
                  <p className="text-base text-gray-500">Loading data</p>
                </div>
              </div>
            ) : (
              <>
                {/* Column headers */}
                <div className="flex-shrink-0 overflow-x-auto bg-white border-b border-[#111C2D33] mx-5 custom-scrollbar-overlay">
                  <table className="w-full min-w-[640px] table-fixed">
                    <thead>
                      <tr className="bg-white">
                        <th className="text-left py-3 sm:py-4 px-4 sm:px-6 text-[#646464] text-xs sm:text-sm font-inter w-[22%]">Client name</th>
                        <th className="text-left py-3 sm:py-4 px-4 sm:px-6 text-[#646464] text-xs sm:text-sm font-inter w-[20%]">Primary nutritionist</th>
                        <th className="text-left py-3 sm:py-4 px-4 sm:px-6 text-[#646464] text-xs sm:text-sm font-inter w-[18%]">Plan name</th>
                        <th className="text-left py-3 sm:py-4 px-4 sm:px-6 text-[#646464] text-xs sm:text-sm font-inter w-[18%]">Goal</th>
                        <th className="text-left py-3 sm:py-4 px-4 sm:px-6 text-[#646464] text-xs sm:text-sm font-inter w-[13%]">Joining date</th>
                        <th className="text-left py-3 sm:py-4 px-4 sm:px-6 text-[#646464] text-xs sm:text-sm font-inter w-[9%]">Duration</th>
                      </tr>
                    </thead>
                  </table>
                </div>

                {/* Scrollable rows */}
                <div className="flex-1 overflow-x-auto overflow-y-auto mx-5 custom-scrollbar-overlay">
                  {clients.length === 0 ? (
                    <div className="flex items-center justify-center h-full py-16">
                      <p className="text-gray-400 text-sm sm:text-base font-inter">
                        {isSearchActive ? "No clients match your search" : "No clients available"}
                      </p>
                    </div>
                  ) : (
                    <table className="w-full min-w-[640px] table-fixed">
                      <tbody>
                        {clients.map((client, index) => (
                          <tr
                            key={client.user_id}
                            onClick={() => handleClientClick(client.user_id)}
                            className="hover:bg-gray-50 transition-colors cursor-pointer"
                          >
                            <td className={`py-3 sm:py-4 px-4 sm:px-6 w-[22%] ${index !== clients.length - 1 ? "border-b border-[#111C2D1A]" : ""}`}>
                              <div className="flex items-center gap-2 sm:gap-3">
                                {getUserAvatar(client)}
                                <div className="font-medium font-inter text-[#1F1F1F] text-sm sm:text-base">
                                  {client.full_name}
                                </div>
                              </div>
                            </td>
                            <td className={`py-3 sm:py-4 px-4 sm:px-6 text-[#1F1F1F] text-sm sm:text-base font-inter w-[20%] ${index !== clients.length - 1 ? "border-b border-[#111C2D1A]" : ""}`}>
                              {client.primary_nutritionist?.full_name?.replace(/^(Mr\.?|Mrs\.?|Ms\.?|Miss\.?|Dr\.?)\s+/i, "") ?? ""}
                            </td>
                            <td className={`py-3 sm:py-4 px-4 sm:px-6 text-[#1F1F1F] text-sm sm:text-base font-inter w-[18%] ${index !== clients.length - 1 ? "border-b border-[#111C2D1A]" : ""}`}>
                              {client.latest_subscription ? trimPlanName(client.latest_subscription.plan_name) : "-"}
                            </td>
                            <td className={`py-3 sm:py-4 px-4 sm:px-6 text-[#1F1F1F] text-sm sm:text-base font-inter w-[18%] ${index !== clients.length - 1 ? "border-b border-[#111C2D1A]" : ""}`}>
                              {client.goal ?? "-"}
                            </td>
                            <td className={`py-3 sm:py-4 px-4 sm:px-6 text-[#1F1F1F] text-sm sm:text-base font-inter w-[13%] ${index !== clients.length - 1 ? "border-b border-[#111C2D1A]" : ""}`}>
                              {formatDate(client.client_since)}
                            </td>
                            <td className={`py-3 sm:py-4 px-4 sm:px-6 text-[#1F1F1F] text-sm sm:text-base font-inter w-[9%] ${index !== clients.length - 1 ? "border-b border-[#111C2D1A]" : ""}`}>
                              {formatDuration(client.latest_subscription?.duration_days)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            resultCount={clients.length}
          />
        </div>
      </div>
    </div>
  );
};

export default SalesClientPage;